/**
 * HTTP client for the admin SPA.
 *
 * Mirrors the signature of `src/app/api/client.ts` in the storefront
 * (`api<T>(endpoint, init): Promise<T>`) so React code reads the same.
 * Reads the JWT from localStorage and attaches `Authorization: Bearer <token>`
 * automatically. Throws `ApiError` on non-2xx so callers can handle errors
 * uniformly.
 */

const TOKEN_KEY = 'drherbal-admin-token'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    // localStorage unavailable (SSR / private mode) — ignore
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public payload?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001').replace(/\/+$/, '')

export async function api<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${BASE}/api${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  }

  if (options.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json'
  }

  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(url, { ...options, headers })

  // 204 No Content — return undefined as unknown
  if (res.status === 204) return undefined as unknown as T

  // Try to parse JSON either way so we can return error messages
  const text = await res.text()
  let payload: unknown = undefined
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = text
    }
  }

  if (!res.ok) {
    const errPayload = payload as Record<string, unknown> | undefined
    const message =
      (errPayload && typeof errPayload.message === 'string' && errPayload.message) ||
      `Request failed with ${res.status}`
    // Auto-logout on 401
    if (res.status === 401) setToken(null)
    throw new ApiError(res.status, message, errPayload)
  }

  return payload as T
}
